import { StorageReference, ref } from 'firebase/storage'
import FilesDropZone, { UploadTaskStatus } from './FilesDropZone'
import { getAppStorage } from '@/firebase/storage-routing'
import { useParams, useSearchParams } from 'react-router-dom'
import { kGcsUserFileUploadsBucketRef } from '@/constants/constants-gcs'
import { getAuth } from 'firebase/auth'
import FilesList from './FilesList'
import { useState, useMemo, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import ContentViewerColumn from '@/content-viewer/components/ContentViewerColumn'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import { ChatBreakpoints } from '../chat-layout-constants'
import ContentViewerSlideover from '@/content-viewer/components/ContentViewerSlideover'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { useViewFile } from './hooks/useViewFile'
import { selectIsContentViewerOpen } from '@/content-viewer/store/selectors'

export default function FilesPage() {
  const { driveName, '*': folderPath } = useParams()
  const dispatch = useAppDispatch()

  // Reload trigger state
  const [reloadTrigger, setReloadTrigger] = useState(0)

  // Get the current user
  const auth = getAuth()
  const uid = auth.currentUser?.uid

  // Derive the currentFolder - memoized to prevent unnecessary rerenders
  const storage = getAppStorage({ bucketUrl: kGcsUserFileUploadsBucketRef, legacy_always_cloud: true })
  const driveRootPath = `users/${uid}`
  const uriFolderPath = folderPath ? folderPath : ''

  const currentFolderRef: StorageReference | null = useMemo(() => ref(storage, `${driveRootPath}/${uriFolderPath}`), [storage, driveRootPath, uriFolderPath])

  // Determine the screen size based on the viewport width and the component's breakpoints
  const { width: viewportWidth } = useViewportSizeInPixels()
  const screenSize = viewportWidth > ChatBreakpoints.SMALL ? ChatBreakpoints.LARGE : ChatBreakpoints.SMALL

  // Open a file in the side viewer when navigated to with ?viewFile= query param
  const [searchParams, setSearchParams] = useSearchParams()
  const viewFile = useViewFile()
  useEffect(() => {
    const fileToView = searchParams.get('viewFile')
    if (fileToView && uid) {
      const fullPath = `users/${uid}/${fileToView}`
      viewFile(fullPath)
      const cleaned = new URLSearchParams(searchParams)
      cleaned.delete('viewFile')
      setSearchParams(cleaned, { replace: true })
    }
  }, [searchParams, setSearchParams, uid, viewFile])

  // Check if there is content to show in the viewer
  const showContentViewer = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))

  // Clear content viewer when component unmounts
  useEffect(() => {
    return () => {
      dispatch(ContentViewerActions.clearContentViewer())
    }
  }, [dispatch])

  // If currentFolderRef is null, we have an storage reference and cannot render
  if (!currentFolderRef) {
    console.error(`Invalid storage reference for drive: ${driveName}, folderPath: ${folderPath}`)

    return (
      <div className={'flex-grow flex flex-col py-4 pr-4 pl-4 lg:pl-0'}>
        <div className="bg-brand-neutral-50 shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6 text-red-700">Error: Invalid storage location</div>
        </div>
      </div>
    )
  }

  return (
    <div className={'flex-grow flex flex-col py-4 pr-4 pl-4 lg:pl-0'}>
      <div className="bg-brand-neutral-50 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <FilesDropZone
            onTaskListItemUpdated={(uploadTask: UploadTaskStatus, allTasks: Map<string, UploadTaskStatus>) => {
              console.log('File upload completed: ', uploadTask.uploadableFile.fileName)

              // Get number of completed tasks to reload when number of completed uploads changes
              const completedTasks = Array.from(allTasks.values()).filter((task) => task.complete)
              setReloadTrigger(completedTasks.length)
            }}
            currentFolder={currentFolderRef}
          />
        </div>
      </div>
      <div className="flex mt-2 bg-brand-neutral-50 shadow rounded-lg flex-1 overflow-hidden">
        {/* Table - full width when no viewer, half width when viewer is shown on desktop */}
        <div className={`${showContentViewer && screenSize !== ChatBreakpoints.SMALL ? 'w-1/2' : 'w-full'} transition-all duration-200 overflow-y-auto h-full`}>
          <FilesList reloadTrigger={reloadTrigger} currentFolderRef={currentFolderRef} />
        </div>

        {/* PDF/Content Viewer - shown when content exists on desktop */}
        {showContentViewer && screenSize !== ChatBreakpoints.SMALL && (
          <div className="w-1/2">
            <ContentViewerColumn conversationId="none" />
          </div>
        )}
      </div>
      {/* Content viewer as slideover - small screens */}
      {screenSize === ChatBreakpoints.SMALL && <ContentViewerSlideover open={showContentViewer} conversationId="none" />}
    </div>
  )
}
